import {searchUrl} from '@github-ui/issue-url-helper'
import type {ProjectPickerBaseProject as ProjectPickerProject} from '@github-ui/item-picker-project'
import {ProjectPicker} from '@github-ui/item-picker-project-relay'
import {ProjectIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import type React from 'react'
import {useCallback, useMemo} from 'react'

import {LABELS} from '../../../constants/labels'
import {useQueryContext, useQueryEditContext} from '../../../contexts/QueryContext'
import {getTokensByType, replaceTokensDifferentially} from '../../../utils/urls'
import type {FilterBarPickerProps} from './ListItemsHeaderWithoutBulkActions'
import styles from './ListProjectFilter.module.css'

export function ListProjectFilter({repo, applySectionFilter, nested}: FilterBarPickerProps) {
  const {activeSearchQuery, currentViewId} = useQueryContext()
  const {debouncedDirtySearchQuery} = useQueryEditContext()

  const query = debouncedDirtySearchQuery ?? activeSearchQuery

  const currentProjectsTokens = useMemo(() => getTokensByType(query, 'project'), [query])

  const onSelectionChanged = useCallback(
    (selectedProjects: ProjectPickerProject[]) => {
      const projects = selectedProjects.map(project => `${repo.owner}/${project.number}`)
      const newQuery = replaceTokensDifferentially(query, projects, 'project')
      if (newQuery !== query) {
        const url = searchUrl({viewId: currentViewId, query: newQuery})
        applySectionFilter(newQuery, url)
      }
    },
    [query, applySectionFilter, currentViewId, repo.owner],
  )

  /**
   *  the approach used here has a limitation described in https://github.com/github/issues/issues/10329
   * if the search bar has a project that is not in the projects that are fetch on first render, the pre-selection won't work
   */

  const currentSelectedProjects = useCallback(
    (projects: ProjectPickerProject[]) => {
      const selectedProjects: ProjectPickerProject[] = []
      currentProjectsTokens?.map(token => {
        const foundProject = projects.find(project => {
          const [repoName, number] = token.split('/')
          return project.url.endsWith(`/${repoName}/projects/${number}`)
        })
        if (foundProject && foundProject.id && !selectedProjects.includes(foundProject)) {
          selectedProjects.push(foundProject)
        }
      })

      return selectedProjects
    },
    [currentProjectsTokens],
  )

  return (
    <ProjectPicker
      pickerId="list-project-filter"
      anchorElement={nested ? NestedProjectsAnchor : ProjectsAnchor}
      repo={repo.name}
      owner={repo.owner}
      onSave={onSelectionChanged}
      selectedProjects={[]}
      readonly={false}
      getSelectedProjects={currentSelectedProjects}
      title={LABELS.filters.projectsLabel}
    />
  )
}

function NestedProjectsAnchor(props: React.HTMLAttributes<HTMLElement>) {
  return (
    <ActionList.Item {...props} aria-label={LABELS.filters.projectsLabel} role="menuitem">
      <ActionList.LeadingVisual>
        <ProjectIcon />
      </ActionList.LeadingVisual>
      {LABELS.filters.projects}...
    </ActionList.Item>
  )
}

function ProjectsAnchor(props: React.HTMLAttributes<HTMLButtonElement>) {
  return (
    <Button
      variant="invisible"
      data-testid="projects-anchor-button"
      trailingVisual={TriangleDownIcon}
      aria-label={LABELS.filters.projectsLabel}
      className={styles.projectFilterButton}
      {...props}
    >
      {LABELS.filters.projects}
    </Button>
  )
}

try{ ListProjectFilter.displayName ||= 'ListProjectFilter' } catch {}
try{ NestedProjectsAnchor.displayName ||= 'NestedProjectsAnchor' } catch {}
try{ ProjectsAnchor.displayName ||= 'ProjectsAnchor' } catch {}